/**
 * BackLink
 *
 * Standard "← Back" text link used to return from a sub-view to a parent view.
 * Works two ways:
 *   - route navigation:  <BackLink to="/dashboard/properties">Back to Properties</BackLink>
 *   - in-page state:     <BackLink onClick={() => setView('funnel')}>Funnel</BackLink>
 *
 * Styled as a subtle text link with a leading arrow. Tints to the tool accent
 * (`--tool-color`) inside a themed ToolPage, falling back to the default purple.
 */

import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import "./BackLink.scss";

const BackLink = ({ to, onClick, children = "Back", icon: Icon = FaArrowLeft, className = "" }) => {
  const content = (
    <>
      <Icon className="back-link__icon" aria-hidden="true" />
      <span>{children}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={`back-link ${className}`}>
        {content}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={`back-link back-link--button ${className}`}>
      {content}
    </button>
  );
};

BackLink.propTypes = {
  /** Route to navigate to (renders a router Link). Omit for an in-page button. */
  to: PropTypes.string,
  /** Click handler (used for in-page back, or alongside `to`). */
  onClick: PropTypes.func,
  children: PropTypes.node,
  /** Leading icon component (defaults to a left arrow). */
  icon: PropTypes.elementType,
  className: PropTypes.string,
};

export default BackLink;
